import React, { createContext, useContext, useState, useCallback } from "react";

const RequestDataContext = createContext();

// The extractor and this editor use different names for the same body types. Normalize on the
// way in, or an unknown type renders an empty Body panel with no tab selected.
const BODY_TYPE_ALIASES = {
  "x-www-form-urlencoded": "form-urlencoded",
  urlencoded: "form-urlencoded",
  formdata: "form-data",
  "multipart/form-data": "form-data",
  raw: "text",
  graphql: "json",
  file: "binary",
};

const EDITOR_BODY_TYPES = ["none", "json", "xml", "text", "form-data", "form-urlencoded", "binary"];

export const normalizeBodyType = (bodyType) => {
  if (!bodyType || typeof bodyType !== "string") return "json";

  const raw = bodyType.trim().toLowerCase();
  if (!raw || raw === "null" || raw === "undefined") return "json";

  const aliased = BODY_TYPE_ALIASES[raw] || raw;
  return EDITOR_BODY_TYPES.includes(aliased) ? aliased : "json";
};

// `bodySubType` can arrive as the literal string "null". Treat it as absent.
export const normalizeBodySubType = (bodySubType, normalizedBodyType) => {
  const raw = typeof bodySubType === "string" ? bodySubType.trim().toLowerCase() : "";

  if (!raw || raw === "null" || raw === "undefined") {
    return normalizedBodyType === "form-data" ? "text" : normalizedBodyType;
  }

  // The extractor prompt emits text-only/file-only; the editor uses text/file.
  if (raw === "text-only") return "text";
  if (raw === "file-only") return "file";

  return raw;
};

export const useRequestData = () => {
  const context = useContext(RequestDataContext);
  if (!context) {
    throw new Error("useRequestData must be used within a RequestDataProvider");
  }
  return context;
};

// `disabledParams` is stored as a JSON string (or object) shaped like `{ headers: {...} }`.
const extractDisabledHeaders = (disabledParams) => {
  if (!disabledParams) return "{}";
  try {
    const parsed = typeof disabledParams === "string" ? JSON.parse(disabledParams) : disabledParams;
    const disabledHeaders = parsed && typeof parsed === "object" ? parsed.headers : null;
    return disabledHeaders && typeof disabledHeaders === "object"
      ? JSON.stringify(disabledHeaders, null, 2)
      : "{}";
  } catch (error) {
    console.warn("Failed to parse disabledParams:", error);
    return "{}";
  }
};

// A `%` that isn't a valid escape (common in example values) makes decodeURIComponent throw.
const safeDecode = (value) => {
  if (!value) return "";
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

// The Body panel stores form-urlencoded content as `a=1&b=2`, but extractor-persisted bodies
// are a flat JSON object.
const toUrlEncodedString = (body) => {
  if (!body) return "";

  let source = body;
  if (typeof source === "string") {
    const trimmed = source.trim();
    if (!trimmed.startsWith("{")) return trimmed;
    try {
      source = JSON.parse(trimmed);
    } catch {
      return trimmed;
    }
  }

  if (typeof source !== "object" || source === null || Array.isArray(source)) return "";

  return Object.entries(source)
    .map(
      ([key, val]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(val === null || val === undefined ? "" : String(val))}`,
    )
    .join("&");
};

export const RequestDataProvider = ({ children }) => {
  // Current selection state
  const [selectedTestCase, setSelectedTestCase] = useState(null);
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);

  // Request data state
  const [method, setMethod] = useState("GET");
  const [url, setUrl] = useState("");
  const [headers, setHeaders] = useState("");
  // Headers the user unchecked in the editor - kept separate from `headers` (which only ever
  // holds enabled headers) so a disabled header survives save/reload instead of being dropped.
  const [disabledHeaders, setDisabledHeaders] = useState("{}");
  const [queryParams, setQueryParams] = useState("");
  const [pathParams, setPathParams] = useState("");
  const [body, setBody] = useState("");
  const [bodyType, setBodyType] = useState("json");
  const [bodySubType, setBodySubType] = useState("json");

  const [preRequestScript, setPreRequestScript] = useState("");
  const [postResponseScript, setPostResponseScript] = useState("");

  // Body content storage for each body type
  const [bodyContentByType, setBodyContentByType] = useState({
    json: "",
    xml: "",
    text: "",
    "form-data": "",
    "form-urlencoded": "",
    binary: "",
  });

  const prettyPrintJson = (value) => {
    if (!value || typeof value !== "string") return value;
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  };

  // Form field states
  const [formFields, setFormFields] = useState([
    {
      key: "",
      value: "",
      type: "text",
      src: null,
      // contentType: null,
      description: null,
      required: false,
      fileName: null,
      fileSize: null,
      fileObject: null,
    },
  ]);
  const [urlEncodedFields, setUrlEncodedFields] = useState([{ key: "", value: "" }]);

  // Helper function to get current source data (test case takes priority)
  const getCurrentSourceData = useCallback(() => {
    return selectedTestCase || selectedEndpoint;
  }, [selectedTestCase, selectedEndpoint]);

  // Helper function to parse form data
  const parseFormData = useCallback((data, bodyType) => {
    if (!data || !data.body) return;

    try {
      let parsedData;
      if (typeof data.body === "string") {
        if (!data.body.trim()) return;
        parsedData = JSON.parse(data.body);
      } else if (typeof data.body === "object") {
        parsedData = data.body;
      } else {
        return;
      }

      if (parsedData.fields && Array.isArray(parsedData.fields)) {
        const fields = parsedData.fields.map((field) => ({
          key: field.key || "",
          value: field.value || "",
          type: field.type || "text",
          src: field.src || null,
          // contentType: field.contentType || null,
          description: field.description || null,
          required: field.required || false,
          fileName: field.fileName || null,
          fileSize: field.fileSize || null,
          fileObject: null,
        }));
        setFormFields(
          fields.length > 0
            ? fields
            : [
                {
                  key: "",
                  value: "",
                  type: "text",
                  src: null,
                  // contentType: null,
                  description: null,
                  required: false,
                  fileName: null,
                  fileSize: null,
                  fileObject: null,
                },
              ],
        );
      } else if (typeof parsedData === "object" && !Array.isArray(parsedData)) {
        const fields = Object.entries(parsedData).map(([key, val]) => ({
          key: String(key),
          value: String(val),
          type: "text",
          src: null,
          // contentType: null,
          description: null,
          required: false,
          fileName: null,
          fileSize: null,
          fileObject: null,
        }));
        setFormFields(
          fields.length > 0
            ? fields
            : [
                {
                  key: "",
                  value: "",
                  type: "text",
                  src: null,
                  // contentType: null,
                  description: null,
                  required: false,
                  fileName: null,
                  fileSize: null,
                  fileObject: null,
                },
              ],
        );
      }
    } catch (error) {
      console.error("Error parsing form-data JSON:", error);
      setFormFields([
        {
          key: "",
          value: "",
          type: "text",
          src: null,
          // contentType: null,
          description: null,
          required: false,
          fileName: null,
          fileSize: null,
          fileObject: null,
        },
      ]);
    }
  }, []);

  // Bodies reach us in two shapes: the `a=1&b=2` string this editor writes on save, and the
  // flat JSON object the extractor persists for bodyType="x-www-form-urlencoded".
  const parseUrlEncodedData = useCallback((data) => {
    if (!data || !data.body) return;

    const toFields = (obj) =>
      Object.entries(obj).map(([key, val]) => ({
        key: String(key),
        value: val === null || val === undefined ? "" : String(val),
      }));

    try {
      let pairs = [];

      if (typeof data.body === "object" && data.body !== null) {
        pairs = toFields(data.body);
      } else if (typeof data.body === "string") {
        const trimmed = data.body.trim();
        let parsedObject = null;

        if (trimmed.startsWith("{")) {
          try {
            const parsed = JSON.parse(trimmed);
            if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
              parsedObject = parsed;
            }
          } catch {
            parsedObject = null;
          }
        }

        if (parsedObject) {
          pairs = toFields(parsedObject);
        } else if (trimmed.includes("=")) {
          pairs = trimmed.split("&").map((pair) => {
            const [key, ...rest] = pair.split("=");
            return {
              key: safeDecode(key),
              value: safeDecode(rest.join("=")),
            };
          });
        }
      }

      setUrlEncodedFields(pairs.length > 0 ? pairs : [{ key: "", value: "" }]);
    } catch (error) {
      console.error("Error parsing form-urlencoded data:", error);
      setUrlEncodedFields([{ key: "", value: "" }]);
    }
  }, []);

  // Function to load test case data
  const loadTestCaseData = useCallback(
    (testCase) => {
      if (!testCase) {
        // Clear all test case data when null is passed
        setSelectedTestCase(null);
        setSelectedEndpoint(null);

        // Reset to default values
        setMethod("GET");
        setUrl("");
        setHeaders("{}");
        setDisabledHeaders("{}");
        setQueryParams("{}");
        setPathParams("{}");
        setBody("");
        setBodyType("json");
        setBodySubType("json");

        // Clear all body content
        setBodyContentByType({
          json: "",
          xml: "",
          text: "",
          "form-data": "",
          "form-urlencoded": "",
          binary: "",
        });

        // Clear form fields
        setFormFields([{ key: "", value: "", type: "text" }]);
        setUrlEncodedFields([{ key: "", value: "" }]);

        // Load scripts from test case data
        let preRequestScriptContent = "";
        let postResponseScriptContent = "";

        if (testCase?.preRequest) {
          preRequestScriptContent = testCase?.preRequest;
        }
        if (testCase?.postResponse) {
          postResponseScriptContent = testCase?.postResponse;
        }

        setPreRequestScript(preRequestScriptContent);
        setPostResponseScript(postResponseScriptContent);
        return;
      }

      setSelectedTestCase(testCase);
      setSelectedEndpoint(null); // Clear endpoint when test case is selected

      // Set basic request data
      setMethod(testCase.httpMethod || "GET");
      setUrl(testCase.endpoint || "");

      // Ensure headers are consistently formatted as JSON string
      const testCaseHeaders = testCase.headers || "{}";
      const formattedHeaders =
        typeof testCaseHeaders === "string"
          ? testCaseHeaders
          : JSON.stringify(testCaseHeaders, null, 2);
      setHeaders(formattedHeaders);
      setDisabledHeaders(extractDisabledHeaders(testCase.disabledParams));

      // Ensure query params are consistently formatted as JSON string
      const testCaseQueryParams = testCase.queryParams || "{}";
      const formattedQueryParams =
        typeof testCaseQueryParams === "string"
          ? testCaseQueryParams
          : JSON.stringify(testCaseQueryParams, null, 2);
      setQueryParams(formattedQueryParams);

      const testCasePathParams = testCase.pathParams || "{}";
      const formattedPathParams =
        typeof testCasePathParams === "string"
          ? testCasePathParams
          : JSON.stringify(testCasePathParams, null, 2);
      setPathParams(formattedPathParams);

      const testCaseBodyType = normalizeBodyType(testCase.bodyType);

      setBody(prettyPrintJson(testCase.body) || "");
      setBodyType(testCaseBodyType);
      setBodySubType(normalizeBodySubType(testCase.bodySubType, testCaseBodyType));

      // Set body content for the specific body type
      const newBodyContent = {
        json: "",
        xml: "",
        text: "",
        "form-data": "",
        "form-urlencoded": "",
        binary: "",
        [testCaseBodyType]:
          testCaseBodyType === "form-urlencoded"
            ? toUrlEncodedString(testCase.body)
            : prettyPrintJson(testCase.body) || "",
      };
      setBodyContentByType(newBodyContent);

      // Load scripts from test case data
      let preRequestScriptContent = "";
      let postResponseScriptContent = "";

      if (testCase?.preRequest) {
        preRequestScriptContent = testCase?.preRequest;
      }
      if (testCase?.postResponse) {
        postResponseScriptContent = testCase.postResponse;
      }

      setPreRequestScript(preRequestScriptContent);
      setPostResponseScript(postResponseScriptContent);

      // Parse form fields if needed
      if (testCaseBodyType === "form-data") {
        parseFormData(testCase, "form-data");
      } else if (testCaseBodyType === "form-urlencoded") {
        parseUrlEncodedData(testCase);
      } else {
        setFormFields([{ key: "", value: "", type: "text" }]);
        setUrlEncodedFields([{ key: "", value: "" }]);
      }
    },
    [parseFormData, parseUrlEncodedData],
  );

  // Function to load endpoint data
  const loadEndpointData = useCallback(
    (endpoint) => {
      if (!endpoint) return;

      setSelectedEndpoint(endpoint);
      setSelectedTestCase(null); // Clear test case when endpoint is selected

      // Set basic request data (URL will be set by the calling component)
      setMethod(endpoint.method || "GET");

      // Ensure headers are consistently formatted as JSON string
      const endpointHeaders = endpoint.headers || "{}";
      const formattedHeaders =
        typeof endpointHeaders === "string"
          ? endpointHeaders
          : JSON.stringify(endpointHeaders, null, 2);
      setHeaders(formattedHeaders);
      setDisabledHeaders(extractDisabledHeaders(endpoint.disabledParams));

      // Ensure query params are consistently formatted as JSON string
      const endpointQueryParams = endpoint.queryParams || "{}";
      const formattedQueryParams =
        typeof endpointQueryParams === "string"
          ? endpointQueryParams
          : JSON.stringify(endpointQueryParams, null, 2);
      setQueryParams(formattedQueryParams);

      const endpointPathParams = endpoint.pathParams || "{}";
      const formattedPathParams =
        typeof endpointPathParams === "string"
          ? endpointPathParams
          : JSON.stringify(endpointPathParams, null, 2);
      setPathParams(formattedPathParams);

      const endpointBodyType = normalizeBodyType(endpoint.bodyType);

      setBody(prettyPrintJson(endpoint.body) || "");
      setBodyType(endpointBodyType);
      setBodySubType(normalizeBodySubType(endpoint.bodySubType, endpointBodyType));

      // Load scripts from endpoint data
      let preRequestScriptContent = "";
      let postResponseScriptContent = "";

      if (endpoint?.preRequest) {
        preRequestScriptContent = endpoint?.preRequest;
      }
      if (endpoint?.postResponse) {
        postResponseScriptContent = endpoint?.postResponse;
      }

      setPreRequestScript(preRequestScriptContent);
      setPostResponseScript(postResponseScriptContent);

      // Set body content for the specific body type
      const newBodyContent = {
        json: "",
        xml: "",
        text: "",
        "form-data": "",
        "form-urlencoded": "",
        binary: "",
        [endpointBodyType]:
          endpointBodyType === "form-urlencoded"
            ? toUrlEncodedString(endpoint.body)
            : prettyPrintJson(endpoint.body) || "",
      };
      setBodyContentByType(newBodyContent);

      // Parse form fields if needed
      if (endpointBodyType === "form-data") {
        parseFormData(endpoint, "form-data");
      } else if (endpointBodyType === "form-urlencoded") {
        parseUrlEncodedData(endpoint);
      } else {
        setFormFields([{ key: "", value: "", type: "text" }]);
        setUrlEncodedFields([{ key: "", value: "" }]);
      }
    },
    [parseFormData, parseUrlEncodedData],
  );

  // Function to update body content for a specific type
  const updateBodyContent = useCallback((bodyType, content, fileObject = null) => {
    setBodyContentByType((prev) => ({
      ...prev,
      [bodyType]: {
        content: content,
        fileObject: fileObject,
      },
    }));
  }, []);

  // Function to get body content for a specific type
  const getBodyContent = useCallback(
    (bodyType) => {
      const data = bodyContentByType[bodyType];

      if (data && typeof data === "object" && "content" in data) {
        return data.fileObject ? data : data.content;
      }
      return data || "";
    },
    [bodyContentByType],
  );

  const getBodyFileObject = useCallback(
    (type) => {
      const data = bodyContentByType[type];
      if (data && typeof data === "object" && "fileObject" in data) {
        return data.fileObject;
      }
      return null;
    },
    [bodyContentByType],
  );

  // Function to clear all data
  const clearAllData = useCallback(() => {
    setSelectedTestCase(null);
    setSelectedEndpoint(null);
    setMethod("GET");
    setUrl("");
    setHeaders("");
    setDisabledHeaders("{}");
    setQueryParams("");
    setPathParams("");
    setBody("");
    setBodyType("json");
    setBodySubType("json");
    setBodyContentByType({
      json: "",
      xml: "",
      text: "",
      "form-data": "",
      "form-urlencoded": "",
      binary: "",
    });
    setFormFields([{ key: "", value: "", type: "text" }]);
    setUrlEncodedFields([{ key: "", value: "" }]);
  }, []);

  const value = {
    // Current selections
    selectedTestCase,
    selectedEndpoint,
    getCurrentSourceData,

    // Request data
    method,
    setMethod,
    url,
    setUrl,
    headers,
    setHeaders,
    disabledHeaders,
    setDisabledHeaders,
    queryParams,
    setQueryParams,
    pathParams,
    setPathParams,
    body,
    setBody,
    bodyType,
    setBodyType,
    bodySubType,
    setBodySubType,

    // Body content management
    bodyContentByType,
    updateBodyContent,
    getBodyContent,
    getBodyFileObject,

    // Form fields
    formFields,
    setFormFields,
    urlEncodedFields,
    setUrlEncodedFields,

    // Script state
    preRequestScript,
    setPreRequestScript,
    postResponseScript,
    setPostResponseScript,

    // Data loading functions
    loadTestCaseData,
    loadEndpointData,
    clearAllData,
  };

  return <RequestDataContext.Provider value={value}>{children}</RequestDataContext.Provider>;
};
